<template>
  <v-row justify="center">
    <v-dialog v-model="active" persistent scrollable max-width="500">
      <ValidationObserver ref="obs">
        <v-card>
          <v-progress-linear v-if="isLoading" indeterminate color="primary" rounded height="6" />

          <v-card-title class="pa-0 shadow-1">
            <v-card-title class="font-weight-medium primary--text">
              Informe personalizado
            </v-card-title>
          </v-card-title>

          <vuescroll :ops="scrollOptions">
            <v-card-text style="max-height: 350px">
              <v-form>
                <v-row dense justify="center">
                  <!-- Range Date Field -->
                  <v-col cols="12">
                    <v-menu
                      ref="dateRangeMenu"
                      v-model="dateRangeMenu"
                      :close-on-content-click="false"
                      :return-value.sync="dates"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="dates"
                          required
                          filled
                          rounded
                          class="rounded-small"
                          label="Rango de fecha"
                          readonly
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        locale="es-us"
                        v-model="dates"
                        :max="module !== 'appointments' ? TODAY_DATE : null"
                        range
                        scrollable
                        no-title
                      >
                        <v-spacer />
                        <v-hover v-slot="{ hover }">
                          <v-btn
                            text
                            rounded
                            class="mr-1"
                            :color="hover ? 'error' : 'grey'"
                            @click="dateRangeMenu = false"
                          >
                            Cancelar
                          </v-btn>
                        </v-hover>

                        <v-hover v-slot="{ hover }">
                          <v-btn
                            rounded
                            :loading="isLoading"
                            :disabled="isLoading"
                            color="success"
                            :class="hover ? 'shadow-2' : 'shadow-4'"
                            class="px-8"
                            @click="handleDateConfirm"
                          >
                            ok
                          </v-btn>
                        </v-hover>
                      </v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Queue Switch -->
                  <v-col cols="12" v-if="isQueueModule">
                    <v-switch v-model="searchByHour" class="mt-0" label="Búsqueda por hora específica" hide-details />
                  </v-col>

                  <!-- Time Picker Menu -->
                  <v-slide-y-transition>
                    <v-col cols="12" v-if="searchByHour">
                      <v-menu
                        ref="menuHour"
                        v-model="hour"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="time"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            v-model="time"
                            label="Seleccionar hora"
                            required
                            filled
                            rounded
                            class="rounded-small"
                            readonly
                            v-on="on"
                          />
                        </template>
                        <v-time-picker
                          v-if="hour"
                          v-model="time"
                          full-width
                          :allowed-minutes="(m) => m % 60 === 0"
                          @click:minute="$refs.menuHour.save(time)"
                        />
                      </v-menu>
                    </v-col>
                  </v-slide-y-transition>

                  <!-- Queue Selection -->
                  <v-col cols="12" v-if="module === 'agentsQueue'">
                    <ValidationProvider rules="required" name="Colas" v-slot="{ errors }" tag="div">
                      <v-select
                        v-model="queue"
                        :disabled="isLoading || isSupervisor"
                        :items="AllQueues"
                        :error-messages="errors"
                        :item-value="queues ? 'item' : 'qname'"
                        :item-text="isSupervisor ? 'number' : 'item'"
                        class="rounded-small"
                        label="Colas"
                        prepend-inner-icon="mdi-database"
                        color="info"
                        multiple
                        required
                        filled
                        rounded
                        chips
                        deletable-chips
                        dense
                      >
                        <template v-slot:item="{ item }">
                          <template v-if="!queues"> {{ item.name }} ({{ item.qname || "" }}) </template>
                          <template v-else>{{ item }}</template>
                        </template>
                      </v-select>
                    </ValidationProvider>
                  </v-col>
                </v-row>

                <!-- Filters Section -->
                <v-slide-y-transition>
                  <v-row dense v-if="filters">
                    <v-col cols="12" v-if="isQueueModule">
                      <v-select
                        v-model="queue"
                        :disabled="isLoading || isSupervisor"
                        multiple
                        :items="AllQueues"
                        :item-text="getQueueItemText"
                        item-value="qname"
                        required
                        filled
                        rounded
                        chips
                        deletable-chips
                        class="rounded-small"
                        dense
                        label="Colas"
                        prepend-inner-icon="mdi-database"
                        hide-details
                        color="info"
                      >
                        <template v-slot:prepend-item>
                          <v-list-item ripple @mousedown.prevent @click="toggleSelectAllQueues">
                            <v-list-item-action>
                              <v-icon :color="AllQueues.length > 0 ? 'indigo darken-4' : ''">
                                {{ icon }}
                              </v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>Seleccionar todo</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider class="mt-2" />
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" v-if="isQueueModule">
                      <v-select
                        v-model="queueType"
                        :disabled="isLoading"
                        :items="timeOptions"
                        item-text="name"
                        item-value="value"
                        filled
                        rounded
                        class="rounded-small"
                        dense
                        label="Tiempo"
                        prepend-inner-icon="mdi-shape"
                        hide-details
                        color="info"
                      />
                    </v-col>

                    <v-col cols="12" v-if="isCdrModule">
                      <v-switch
                        v-model="numberOption"
                        :label="numberOption ? 'Búsqueda por número' : 'Búsqueda por extensión'"
                        hide-details
                      />
                    </v-col>

                    <v-col cols="12" v-if="showExtensionsField">
                      <v-autocomplete
                        v-model="extensionInput"
                        multiple
                        :items="extensionItems"
                        item-text="extension"
                        item-value="extension"
                        required
                        :disabled="isLoading"
                        filled
                        rounded
                        chips
                        class="rounded-small"
                        dense
                        label="Extensiones"
                        prepend-inner-icon="mdi-headset"
                        hide-details
                        color="info"
                      />
                    </v-col>

                    <v-col cols="12" v-if="isCdrModule && !numberOption">
                      <v-autocomplete
                        v-model="statusCall"
                        :items="statusItems"
                        item-text="text"
                        item-value="value"
                        required
                        :disabled="isLoading"
                        filled
                        rounded
                        class="rounded-small"
                        dense
                        label="Estados"
                        hide-details
                        color="info"
                      />
                    </v-col>

                    <v-col cols="12" v-if="isCdrModule && numberOption">
                      <v-text-field
                        v-model="number"
                        required
                        filled
                        rounded
                        class="rounded-small"
                        dense
                        label="Número telefónico"
                        prepend-inner-icon="mdi-phone"
                        hide-details
                        color="info"
                      />
                    </v-col>

                    <v-col cols="12" v-if="showCallTypeField">
                      <v-select
                        v-model="callType"
                        :items="callTypeItems"
                        item-text="text"
                        item-value="value"
                        required
                        filled
                        rounded
                        class="rounded-small"
                        dense
                        label="Tipo de llamada"
                        prepend-inner-icon="mdi-phone"
                        hide-details
                        color="info"
                      />
                    </v-col>

                    <v-col cols="12" v-if="isQueueModule">
                      <v-select
                        v-model="callTypeQueue"
                        :items="callTypeQueueItems"
                        item-text="text"
                        item-value="value"
                        required
                        filled
                        rounded
                        class="rounded-small"
                        dense
                        label="Tipo de llamada"
                        prepend-inner-icon="mdi-phone"
                        hide-details
                        color="info"
                      />
                    </v-col>

                    <v-col cols="12" v-if="module === 'reportCalls' && callType === 1">
                      <v-select
                        v-model="callTypeReportCalls"
                        :items="callTypeReportCallsItems"
                        item-text="text"
                        item-value="value"
                        required
                        filled
                        rounded
                        class="rounded-small"
                        dense
                        label="Clasificación de llamada"
                        prepend-inner-icon="mdi-tag"
                        hide-details
                        color="info"
                      />
                    </v-col>
                  </v-row>
                </v-slide-y-transition>
              </v-form>
            </v-card-text>
          </vuescroll>

          <v-divider />

          <!-- Actions -->
          <v-card-actions class="mx-2">
            <v-menu top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-if="module !== 'chatReport'" color="success" v-bind="attrs" v-on="on" small rounded fab>
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item @click="downloadCsv(false)" v-if="showStandardCsvOption">
                  <v-list-item-title>CSV</v-list-item-title>
                </v-list-item>

                <template v-if="isQueueModule">
                  <v-list-item @click="downloadCsv(false, 6)"
                    ><v-list-item-title>Time Out</v-list-item-title></v-list-item
                  >
                  <v-list-item @click="downloadCsv(false, 1)"
                    ><v-list-item-title>Ingresadas</v-list-item-title></v-list-item
                  >
                  <v-list-item @click="downloadCsv(false, 2)"
                    ><v-list-item-title>Contestadas</v-list-item-title></v-list-item
                  >
                  <v-list-item @click="downloadCsv(false, 3)"
                    ><v-list-item-title>Abandonadas</v-list-item-title></v-list-item
                  >
                  <v-list-item @click="downloadCsv(false, 4)"
                    ><v-list-item-title>Transferidas</v-list-item-title></v-list-item
                  >
                  <v-list-item @click="downloadCsv(false, 5)"
                    ><v-list-item-title>Intentos no atendidos</v-list-item-title></v-list-item
                  >
                  <v-list-item @click="downloadCsv(false, 0)"><v-list-item-title>Todos</v-list-item-title></v-list-item>
                </template>

                <template v-if="isCdrModule">
                  <v-list-item @click="downloadCsv(true)"><v-list-item-title>Encuestas</v-list-item-title></v-list-item>
                  <v-list-item @click="downloadFiltered"><v-list-item-title>Filtrado</v-list-item-title></v-list-item>
                </template>
              </v-list>
            </v-menu>

            <v-spacer />

            <v-hover v-slot="{ hover }" v-if="module !== 'testReport'">
              <v-btn
                text
                rounded
                class="mr-1"
                :color="hover ? 'error' : 'grey'"
                @click="isSupervisor ? sendformWithDefaultValues() : closedialog('cerrar')"
              >
                Cancelar
              </v-btn>
            </v-hover>

            <v-hover v-slot="{ hover }" v-if="showSearchButton">
              <v-btn
                rounded
                :loading="isLoading"
                :disabled="isLoading"
                color="success"
                :class="hover ? 'shadow-2' : 'shadow-4'"
                class="px-8"
                @click="sendform"
              >
                Buscar
              </v-btn>
            </v-hover>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <import-dialog :module="module" :isOpenDialog="isOpenImport" @dialog:close="isOpenImport = false" />
  </v-row>
</template>

<script>
import vuescroll from "vuescroll";
import axios from "axios";
import moment from "moment";
import { mapState } from "vuex";
import CdrDownloadToCsv from "@/components/Utils/CdrDownloadCsv";
import ImportDialog from "@/components/ImportDialog/ImportDialog.vue";

const TODAY_DATE = moment().format("YYYY-MM-DD");

// Static Option Constants
const CALL_TYPES = [
  { text: "Todas", value: null },
  { text: "Entrantes", value: 2 },
  { text: "Salientes", value: 1 },
];

const CALL_TYPES_QUEUE = [
  { text: "Todas", value: false },
  { text: "Externas", value: true },
];

const CALL_TYPES_REPORT = [
  { text: "Todas", value: null },
  { text: "Internas", value: 0 },
  { text: "Externas", value: 1 },
];

const STATUS_ITEMS = [
  { text: "Todas", value: null },
  { text: "Contestadas", value: "ANSWERED" },
  { text: "No contestadas", value: "NO ANSWER" },
  { text: "Ocupado", value: "BUSY" },
  { text: "Fallido", value: "FAILED" },
];

const TIME_OPTIONS = [
  { name: "Dia", value: "day" },
  { name: "Hora", value: "hour" },
];

const EXCLUDED_MODULES_ON_CREATE = [
  "agentsQueue",
  "agentsCdr",
  "agentsPauses",
  "agentsCallsInformation",
  "productivityReport",
  "ReportCdrChat",
  "ReportCallbacks",
  "agentTimeline",
];

export default {
  name: "DateRangePickerDialog",

  components: { vuescroll, ImportDialog },

  props: {
    active: Boolean,
    id: { type: Number, required: false },
    module: { type: String, required: true },
    type: { type: String, required: false },
    queues: { type: Array, required: false },
    actualRol: { type: Number, required: false },
  },

  data() {
    return {
      TODAY_DATE,
      searchByHour: false,
      isLoading: false,
      isOpenImport: false,
      dateRangeMenu: false,
      filters: true,
      numberOption: false,
      time: null,
      hour: false,
      queue: [],
      queueType: null,
      statusCall: "",
      callType: null,
      callTypeQueue: false,
      callTypeReportCalls: null,
      number: null,
      extensionInput: [],
      dates: [TODAY_DATE, TODAY_DATE],
      scrollOptions: { bar: { background: "#bacfcec0", keepShow: true } },
      callTypeItems: CALL_TYPES,
      callTypeQueueItems: CALL_TYPES_QUEUE,
      callTypeReportCallsItems: CALL_TYPES_REPORT,
      statusItems: STATUS_ITEMS,
      timeOptions: TIME_OPTIONS,
    };
  },

  computed: {
    ...mapState({
      allExtension: (state) => state.callsInfo.allExtension,
      extensionItemsBySupervisor: (state) => state.passport.user.extensions,
    }),

    isSupervisor() {
      return this.CurrentRole === 10;
    },

    CurrentRole() {
      return this.$store.getters["passport/GET_ROLE"];
    },

    ProfileInfo() {
      return this.$store.getters["passport/CURRENT_USER"];
    },

    AllQueues() {
      return this.queues || this.$store.getters["queue/GET_QUEUES"];
    },

    likesAllQueues() {
      return this.queue.length === this.AllQueues.length;
    },

    likesSomeQueues() {
      return this.queue.length > 0 && !this.likesAllQueues;
    },

    icon() {
      if (this.likesAllQueues) return "mdi-close-box";
      if (this.likesSomeQueues) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },

    extensionItems() {
      return this.isSupervisor ? this.extensionItemsBySupervisor || [] : this.allExtension || [];
    },

    extension() {
      return this.isSupervisor ? this.extensionItemsBySupervisor || [] : this.extensionInput;
    },

    // Module Conditionals
    isQueueModule() {
      return this.module === "queue";
    },

    isCdrModule() {
      return this.module === "cdr";
    },

    showExtensionsField() {
      return ["reportCalls", "cdr"].includes(this.module) && !this.numberOption;
    },

    showCallTypeField() {
      return ["reportCalls", "cdr", "agentsCallsInformation"].includes(this.module);
    },

    showSearchButton() {
      return !["testReport", "appointments", "ReportCdrChat", "ReportCallbacks"].includes(this.module);
    },

    showStandardCsvOption() {
      return [
        "accumulatedIndicators",
        "testReport",
        "ReportCdrChat",
        "appointments",
        "reportCalls",
        "cdr",
        "ReportCallbacks",
        "agentTimeline",
      ].includes(this.module);
    },
  },

  watch: {
    AllQueues(val) {
      if (val?.length && this.isSupervisor) {
        this.queue = val.map((el) => el.number);
      }
    },
  },

  created() {
    if (!EXCLUDED_MODULES_ON_CREATE.includes(this.module)) {
      this.getSelectsData();
      this.getExtensionDate();
    }
    this.$store.dispatch("queue/GET_QUEUES", this.isSupervisor);
  },

  methods: {
    normalizeDates() {
      if (this.dates && this.dates.length === 2) {
        if (this.dates[0] > this.dates[1]) {
          this.dates.reverse();
        }
      }
    },

    getQueueItemText(item) {
      if (this.isSupervisor) return item.number;
      return `${item.name} ${item.qname ? `- ${item.qname}` : ""}`;
    },

    toggleSelectAllQueues() {
      this.$nextTick(() => {
        this.queue = this.likesAllQueues ? [] : this.AllQueues.map((item) => item.qname);
      });
    },

    handleDateConfirm() {
      this.normalizeDates();
      this.$refs.dateRangeMenu.save(this.dates);

      const isExcludedModule =
        this.module.includes("agents") ||
        this.module === "ReportCallbacks" ||
        this.module === "ReportCdrChat" ||
        this.module === "agentTimeline";

      if (!isExcludedModule) {
        this.getExtensionDate();
      }
    },

    openImportDialog() {
      this.isOpenImport = true;
    },

    closedialog(value, parameters) {
      this.$emit("dialog:change", value, parameters);
      this.queue = [];
      this.callType = null;
      this.callTypeQueue = false;
      this.number = null;
      this.filters = false;
      this.dates = [];
      this.hour = null;
      this.searchByHour = false;
    },

    async getSelectsData() {
      if (this.isQueueModule) {
        try {
          await this.$store.dispatch("queue/GET_QUEUES");
        } catch {
          this.$snotify.error("No se pudo obtener listado de colas.", "¡Error!");
        }
      }
      this.isLoading = false;
    },

    async getExtensionDate() {
      try {
        this.isLoading = true;
        await this.$store.dispatch("callsInfo/SHOW_EXTENSION", this.dates);
      } catch {
        this.$snotify.error("No se pudo obtener listado de extensiones.", "¡Error!");
      } finally {
        this.isLoading = false;
      }
    },

    async sendform() {
      const isValid = await this.$refs.obs.validate();
      if (!isValid) return;

      this.normalizeDates();

      if (this.module === "chatReport") {
        this.$emit("dialog:change", {
          date: { start: this.dates[0], end: this.dates[1] },
        });
        return;
      }

      this.setLoadingOverlay(true);

      try {
        if (this.isQueueModule) {
          await this.handleQueueFormSubmit();
        } else if (
          ["agentsPauses", "agentsCdr", "agentsCallsInformation", "accumulatedIndicators", "agentTimeline"].includes(
            this.module,
          )
        ) {
          this.handleSimpleModuleSubmit();
        } else {
          await this.executeCustomApiQuery(this.buildCustomRequestPayload());
        }
      } finally {
        this.setLoadingOverlay(false);
      }
    },

    async handleQueueFormSubmit() {
      if (this.searchByHour) {
        const value = this.dates[0];
        this.dates[1] = value;
        this.dates[0] = `${this.dates[0]}T${this.time}:00.000000Z`;
        this.dates[1] = `${this.dates[1]}T${this.time.substring(0, 2)}:59:00.000000Z`;
      } else {
        this.time = null;
      }

      const rq = {
        format: "P",
        value: { start: this.dates[0], end: this.dates[1], queue: this.queue },
        hour: this.searchByHour,
        type: this.queueType,
        externals: this.callTypeQueue,
      };

      const response = await this.$store.dispatch("tail/GET_REPORT", rq);
      this.filters = false;
      this.isLoading = false;
      this.closedialog({
        date: {
          start: this.dates[0],
          end: this.dates[1],
          queue: this.queue,
          type: this.queueType,
          externals: this.callTypeQueue,
        },
        response,
      });
    },

    handleSimpleModuleSubmit() {
      const payloadMap = {
        accumulatedIndicators: { start_date: this.dates[0], end_date: this.dates[1] },
        agentsCallsInformation: { start: this.dates[0], end: this.dates[1], call: this.callType },
        agentTimeline: { start: this.dates[0], end: this.dates[1] },
        default: { start: this.dates[0], end: this.dates[1] },
      };

      const payload = payloadMap[this.module] || payloadMap.default;
      this.closedialog(this.module, payload);
    },

    buildCustomRequestPayload() {
      const request = {
        start: this.dates[0],
        end: this.dates[1],
        extension: this.extension.join("-"),
        call: this.callType,
        status: this.statusCall,
        number: this.number,
      };

      if (this.callType === 1 && this.callTypeReportCalls !== null) {
        request.externals = this.callTypeReportCalls;
      }

      return request;
    },

    async sendformWithDefaultValues() {
      if (["cdr", "reportCalls"].includes(this.module)) {
        const request = {
          start: this.dates[0],
          end: this.dates[1],
          extension: this.extensionItemsBySupervisor.join("-"),
          call: this.callType,
          status: this.statusCall,
          number: this.number,
        };

        this.setLoadingOverlay(true);
        try {
          await this.executeCustomApiQuery(request);
        } finally {
          this.setLoadingOverlay(false);
        }
      }
    },

    async executeCustomApiQuery(request) {
      try {
        const accessToken = localStorage.getItem("token");
        const res = await axios.get(`${process.env.VUE_APP_ROOTAPI}/${this.module}/custom`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: request,
        });

        this.filters = false;
        this.closedialog(res.data, request);
      } catch {
        this.$snotify.error("Hubo un error en la consulta.", "Error");
      } finally {
        this.isLoading = false;
      }
    },

    setLoadingOverlay(value) {
      this.isLoading = value;
      this.$store.commit("layout/SET_OVERLAY", {
        value,
        title: value ? "Cargando" : "",
      });
    },

    async downloadCsv(isAccountCode, queueType) {
      if (this.numberOption && !this.number) return;

      this.normalizeDates();

      let payload = {
        start: this.dates[0],
        end: this.dates[1],
        call: this.callType,
        queue: this.queue,
        extension: this.extension.join("-") || null,
        number: this.numberOption ? this.number : null,
        accountcode: isAccountCode,
      };

      if (this.module === "accumulatedIndicators") {
        payload = { start_date: this.dates[0], end_date: this.dates[1] };
      } else if (this.module === "testReport") {
        payload = { start: this.dates[0], end: this.dates[1], id: this.id };
      } else if (this.module === "ReportCallbacks") {
        payload = { start: this.dates[0], end: this.dates[1] };
      } else if (this.module === "agentTimeline") {
        payload = { start_date: this.dates[0], end_date: this.dates[1], user_id: this.id };
      }

      if (queueType !== undefined && queueType !== 0) {
        payload.type = queueType;
      }

      this.setLoadingOverlay(true);
      try {
        await CdrDownloadToCsv(payload, this.module);
        this.closedialog("cerrar");
      } catch {
        this.$snotify.error("No se pudo descargar los registros seleccionados.", "¡Error!");
      } finally {
        this.setLoadingOverlay(false);
      }
    },

    async downloadFiltered() {
      this.normalizeDates();
      const payload = {
        start_date: this.dates[0],
        end_date: this.dates[1],
      };
      await CdrDownloadToCsv(payload, "cdrFiltered");
    },
  },
};
</script>
